/**
 * CreatorDashboardPage - Earnings and analytics dashboard for preset creators
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DollarSign,
  TrendingUp,
  Package,
  Download,
  CreditCard,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  Sparkles,
  BarChart3,
  PieChart,
  Users,
  Star,
  Home,
  Settings,
  RefreshCw,
  ExternalLink,
  Info,
  Zap,
  Crown,
  Banknote,
  Building,
  Link2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, trend, trendValue, color = 'violet', subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trendValue}
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-white mb-1">{value}</div>
    <div className="text-sm text-zinc-400">{title}</div>
    {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
  </motion.div>
);

// Transaction Row Component
const TransactionRow = ({ transaction, index }) => {
  const isPositive = transaction.type === 'sale' || transaction.net_amount > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0"
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isPositive ? 'bg-emerald-500/20' : 'bg-red-500/20'
        }`}>
          {isPositive ? (
            <ArrowUpRight className="w-4 h-4 text-emerald-400" />
          ) : (
            <ArrowDownRight className="w-4 h-4 text-red-400" />
          )}
        </div>
        <div>
          <div className="text-sm font-medium text-white capitalize">{transaction.type}</div>
          <div className="text-xs text-zinc-500">
            {new Date(transaction.timestamp).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-semibold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
          {isPositive ? '+' : '-'}${Math.abs(transaction.net_amount || transaction.amount || 0).toFixed(2)}
        </div>
        {transaction.commission > 0 && (
          <div className="text-xs text-zinc-500">
            Fee: ${transaction.commission.toFixed(2)}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Subscription Plan Card
const PlanCard = ({ plan, planId, isActive, onSubscribe }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`bg-zinc-900/50 border rounded-xl p-5 ${
      isActive ? 'border-violet-500 ring-2 ring-violet-500/30' : 'border-zinc-800'
    }`}
  >
    {isActive && (
      <div className="flex items-center gap-2 text-xs text-violet-400 mb-3">
        <CheckCircle className="w-4 h-4" />
        Active Plan
      </div>
    )}
    <div className="flex items-center gap-2 mb-2">
      <Crown className="w-5 h-5 text-amber-400" />
      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
    </div>
    <div className="text-2xl font-bold text-white mb-4">
      ${plan.price}<span className="text-sm font-normal text-zinc-400">/month</span>
    </div>
    <ul className="space-y-2 mb-4">
      {plan.features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>
    {!isActive && (
      <Button
        onClick={() => onSubscribe(planId)}
        className="w-full bg-violet-600 hover:bg-violet-500"
        data-testid={`subscribe-${planId}`}
      >
        <Zap className="w-4 h-4 mr-2" />
        Subscribe Now
      </Button>
    )}
  </motion.div>
);

export default function CreatorDashboardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // State
  const [earnings, setEarnings] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscribing, setSubscribing] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  
  // Fetch earnings data
  const fetchEarnings = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/payments/creator/${user.id}/earnings`);
      if (response.ok) {
        const data = await response.json();
        setEarnings(data);
      }
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  }, [user?.id]);
  
  // Fetch payment config
  const fetchPaymentConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/config`);
      if (response.ok) {
        const data = await response.json();
        setPaymentConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
    }
  }, []);
  
  // Fetch Connect status
  const fetchConnectStatus = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/payments/connect/status/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setConnectStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch connect status:', error);
    }
  }, [user?.id]);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEarnings(), fetchPaymentConfig(), fetchConnectStatus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEarnings, fetchPaymentConfig, fetchConnectStatus]);
  
  // Handle Connect onboarding
  const handleConnectOnboard = async () => {
    if (!user) {
      toast.error('Please log in');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/payments/connect/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: user.id,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.onboarding_url;
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start onboarding');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to start Connect onboarding');
      console.error(error);
    }
  };
  
  // Handle payout request
  const handleRequestPayout = async () => {
    if (!user || !payoutAmount) return;
    
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount < 1) {
      toast.error('Minimum payout is $1.00');
      return;
    }
    
    if (amount > (earnings?.available_balance || 0)) {
      toast.error('Amount exceeds available balance');
      return;
    }
    
    setRequestingPayout(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/connect/payout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_id: user.id,
          amount: amount
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setPayoutAmount('');
        fetchEarnings(); // Refresh earnings
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Payout failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to request payout');
      console.error(error);
    } finally {
      setRequestingPayout(false);
    }
  };
  
  // Handle subscription
  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error('Please log in to subscribe');
      navigate('/auth');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/subscription/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Failed to create subscription');
      }
    } catch (error) {
      toast.error('Failed to start subscription');
      console.error(error);
    } finally {
      setSubscribing(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Calculate stats
  const commissionPercent = (earnings?.commission_rate || 0.15) * 100;
  const effectiveRate = 100 - commissionPercent;
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/marketplace')}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-400" />
                  Creator Dashboard
                </h1>
                <p className="text-sm text-zinc-400">Track your earnings and analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { fetchEarnings(); fetchPaymentConfig(); }}
                className="text-zinc-400 hover:text-white"
                title="Refresh"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => navigate('/marketplace/upload')}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <Package className="w-4 h-4 mr-2" />
                Upload Preset
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Overview', icon: Home },
              { id: 'earnings', label: 'Earnings', icon: DollarSign },
              { id: 'payouts', label: 'Payouts', icon: Banknote },
              { id: 'pricing', label: 'Pricing Plans', icon: CreditCard },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-white border-violet-500'
                    : 'text-zinc-400 border-transparent hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatsCard
                  title="Total Earnings"
                  value={`$${(earnings?.total_earnings || 0).toFixed(2)}`}
                  icon={DollarSign}
                  color="emerald"
                  subtitle={`${effectiveRate}% of sales after fees`}
                />
                <StatsCard
                  title="Available Balance"
                  value={`$${(earnings?.available_balance || 0).toFixed(2)}`}
                  icon={Wallet}
                  color="violet"
                  subtitle="Ready for payout"
                />
                <StatsCard
                  title="Total Sales"
                  value={earnings?.total_sales || 0}
                  icon={Download}
                  color="cyan"
                />
                <StatsCard
                  title="Commission Rate"
                  value={`${commissionPercent}%`}
                  icon={PieChart}
                  color="amber"
                  subtitle={earnings?.commission_rate === 0.1 ? 'Creator Pro discount' : 'Standard rate'}
                />
              </div>
              
              {/* Commission Info Banner */}
              {earnings?.commission_rate === 0.15 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-violet-600/20 to-amber-600/20 border border-violet-500/30 rounded-xl p-4 mb-6"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-500/30 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Upgrade to Creator Pro</h3>
                        <p className="text-sm text-zinc-400">Reduce your commission from 15% to just 10%</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setActiveTab('pricing')}
                      className="bg-violet-600 hover:bg-violet-500"
                    >
                      Learn More
                    </Button>
                  </div>
                </motion.div>
              )}
              
              {/* Recent Transactions */}
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-zinc-400" />
                    Recent Transactions
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab('earnings')}
                    className="text-violet-400 hover:text-violet-300"
                  >
                    View All
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                
                {earnings?.recent_transactions?.length > 0 ? (
                  <div className="divide-y divide-zinc-800">
                    {earnings.recent_transactions.slice(0, 5).map((tx, idx) => (
                      <TransactionRow key={idx} transaction={tx} index={idx} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-3">
                      <DollarSign className="w-8 h-8 text-zinc-600" />
                    </div>
                    <p className="text-zinc-400 mb-2">No transactions yet</p>
                    <p className="text-sm text-zinc-500">Upload presets to start earning!</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Earnings Tab */}
          {activeTab === 'earnings' && (
            <motion.div
              key="earnings"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Earnings Summary */}
                <div className="lg:col-span-1">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                    <h2 className="text-lg font-bold text-white mb-4">Earnings Summary</h2>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                        <span className="text-zinc-400">Total Earnings</span>
                        <span className="text-white font-semibold">
                          ${(earnings?.total_earnings || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                        <span className="text-zinc-400">Available Balance</span>
                        <span className="text-emerald-400 font-semibold">
                          ${(earnings?.available_balance || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                        <span className="text-zinc-400">Pending Balance</span>
                        <span className="text-amber-400 font-semibold">
                          ${(earnings?.pending_balance || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <span className="text-zinc-400">Total Sales</span>
                        <span className="text-white font-semibold">{earnings?.total_sales || 0}</span>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-zinc-800">
                      <div className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                        <Info className="w-4 h-4" />
                        Commission Rate
                      </div>
                      <div className="text-xl font-bold text-white">
                        {commissionPercent}%
                        <span className="text-sm font-normal text-zinc-400 ml-2">
                          (you keep {effectiveRate}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Transaction History */}
                <div className="lg:col-span-2">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                    <h2 className="text-lg font-bold text-white mb-4">Transaction History</h2>
                    
                    {earnings?.recent_transactions?.length > 0 ? (
                      <div className="divide-y divide-zinc-800">
                        {earnings.recent_transactions.map((tx, idx) => (
                          <TransactionRow key={idx} transaction={tx} index={idx} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                          <TrendingUp className="w-10 h-10 text-zinc-600" />
                        </div>
                        <p className="text-zinc-400 mb-2">No transactions yet</p>
                        <p className="text-sm text-zinc-500 mb-4">
                          Your sales and earnings will appear here
                        </p>
                        <Button
                          onClick={() => navigate('/marketplace/upload')}
                          className="bg-violet-600 hover:bg-violet-500"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Upload Your First Preset
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Payouts Tab */}
          {activeTab === 'payouts' && (
            <motion.div
              key="payouts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Connect Account Status */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-violet-400" />
                    Stripe Connect Account
                  </h2>
                  
                  {!connectStatus?.connected ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                        <Link2 className="w-8 h-8 text-zinc-600" />
                      </div>
                      <p className="text-zinc-400 mb-2">Connect your bank account</p>
                      <p className="text-sm text-zinc-500 mb-4">
                        Set up Stripe Connect to receive direct payouts from your preset sales
                      </p>
                      <Button
                        onClick={handleConnectOnboard}
                        className="bg-violet-600 hover:bg-violet-500"
                        data-testid="connect-onboard-btn"
                      >
                        <Banknote className="w-4 h-4 mr-2" />
                        Set Up Payouts
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                        <span className="text-zinc-400">Account Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          connectStatus.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {connectStatus.status === 'active' ? 'Active' : 'Pending Setup'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                        <span className="text-zinc-400">Payouts Enabled</span>
                        <span className={connectStatus.payouts_enabled ? 'text-emerald-400' : 'text-amber-400'}>
                          {connectStatus.payouts_enabled ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <Clock className="w-5 h-5" />
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <span className="text-zinc-400">Charges Enabled</span>
                        <span className={connectStatus.charges_enabled ? 'text-emerald-400' : 'text-amber-400'}>
                          {connectStatus.charges_enabled ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <Clock className="w-5 h-5" />
                          )}
                        </span>
                      </div>
                      
                      {connectStatus.status !== 'active' && (
                        <Button
                          onClick={handleConnectOnboard}
                          variant="outline"
                          className="w-full mt-4"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Complete Setup
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Request Payout */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                  <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                    Request Payout
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                      <span className="text-zinc-400">Available Balance</span>
                      <span className="text-2xl font-bold text-emerald-400">
                        ${(earnings?.available_balance || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                      <span className="text-zinc-400">Pending Balance</span>
                      <span className="text-lg text-amber-400">
                        ${(earnings?.pending_balance || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    {connectStatus?.connected && connectStatus?.payouts_enabled ? (
                      <div className="mt-4">
                        <label className="text-sm text-zinc-400 mb-2 block">Payout Amount</label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                            <input
                              type="number"
                              value={payoutAmount}
                              onChange={(e) => setPayoutAmount(e.target.value)}
                              placeholder="0.00"
                              min="1"
                              max={earnings?.available_balance || 0}
                              step="0.01"
                              className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg pl-8 pr-3 py-2 text-white focus:outline-none focus:border-violet-500"
                              data-testid="payout-amount-input"
                            />
                          </div>
                          <Button
                            onClick={handleRequestPayout}
                            disabled={requestingPayout || !payoutAmount || parseFloat(payoutAmount) < 1}
                            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
                            data-testid="request-payout-btn"
                          >
                            {requestingPayout ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              'Request'
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-zinc-500 mt-2">
                          Minimum payout: $1.00 • Transfers take 2-3 business days
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg text-center">
                        <p className="text-zinc-400 text-sm">
                          {!connectStatus?.connected 
                            ? 'Set up your Stripe Connect account to request payouts'
                            : 'Complete your account setup to enable payouts'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Payout Info */}
              <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-zinc-400">
                    <p className="mb-2">
                      <strong className="text-white">How payouts work:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>When someone purchases your preset, 85% (or 90% with Creator Pro) is added to your available balance</li>
                      <li>Request a payout anytime once you have $1.00 or more available</li>
                      <li>Funds are transferred directly to your connected bank account via Stripe</li>
                      <li>Transfers typically complete within 2-3 business days</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Pricing Plans Tab */}
          {activeTab === 'pricing' && paymentConfig && (
            <motion.div
              key="pricing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Creator Plans</h2>
                <p className="text-zinc-400">
                  Upgrade to reduce your commission and unlock premium features
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Free Plan */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                >
                  <h3 className="text-lg font-bold text-white mb-2">Free</h3>
                  <div className="text-2xl font-bold text-white mb-4">
                    $0<span className="text-sm font-normal text-zinc-400">/month</span>
                  </div>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      Upload unlimited presets
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      Basic analytics
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <XCircle className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      <span className="text-zinc-500">15% commission on sales</span>
                    </li>
                  </ul>
                  <div className="text-center text-sm text-zinc-500">Current Plan</div>
                </motion.div>
                
                {/* Creator Pro Plan */}
                {paymentConfig.subscription_plans.creator_pro && (
                  <PlanCard
                    plan={paymentConfig.subscription_plans.creator_pro}
                    planId="creator_pro"
                    isActive={earnings?.commission_rate === 0.1}
                    onSubscribe={handleSubscribe}
                  />
                )}
              </div>
              
              {/* Price Tiers Info */}
              <div className="mt-12">
                <h3 className="text-xl font-bold text-white text-center mb-6">
                  Preset Price Tiers
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
                  {Object.entries(paymentConfig.price_tiers).map(([tier, price]) => (
                    <div
                      key={tier}
                      className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 text-center"
                    >
                      <div className="text-sm font-medium text-zinc-400 capitalize mb-1">{tier}</div>
                      <div className="text-xl font-bold text-white">
                        {price === 0 ? 'Free' : `$${price.toFixed(2)}`}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm text-zinc-500 mt-4">
                  Set your preset prices when uploading or from your preset management page
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
